import {noop, throttle} from 'lodash';

import {globalDocument, globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {isWaitingServiceWorker, reloadServiceWorker} from '../serviceWorker/bridge/serviceWorkerBridge';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../tracking';

const lastActiveTimeStorageKey = 'lastActiveTime';
let idleUpdateBlockerCount = 0;
let setLastActiveTimeIntervalId = 0;
let isIdleUpdateEnabled = false;
let broadcastChannel: BroadcastChannel | undefined;
let lastActiveTime = Date.now();

function setLastActiveTime() {
  try {
    lastActiveTime = Date.now();
    if (broadcastChannel) {
      broadcastChannel.postMessage({});
    } else {
      localStorage.setItem(lastActiveTimeStorageKey, String(lastActiveTime));
    }
  } catch (error) {
    reportToBugsnag(error);
  }
}

const setLastActiveTimeThrottled = throttle(setLastActiveTime, 60 * 1000);

export function startIdleUpdateBlocker() {
  if (!isIdleUpdateEnabled) {
    return noop;
  }

  if (idleUpdateBlockerCount++ === 0) {
    setLastActiveTimeThrottled();
    setLastActiveTimeIntervalId = globalWindow.setInstrumentedInterval(setLastActiveTimeThrottled, 60 * 1000);
  }

  return () => {
    if (--idleUpdateBlockerCount === 0) {
      globalWindow.clearInterval(setLastActiveTimeIntervalId);
    }
  };
}

function setupUserInputIdleBlocker() {
  globalDocument.addEventListener('mousedown', setLastActiveTimeThrottled, true);
  globalDocument.addEventListener('keydown', setLastActiveTimeThrottled, true);
}

function setupVisibilityIdleBlocker() {
  let stopVisibilityIdleBlocker: Handler | undefined;

  globalDocument.addEventListener('visibilitychange', () => {
    // Block idle updates if visible
    if (!globalDocument.hidden && !stopVisibilityIdleBlocker) {
      stopVisibilityIdleBlocker = startIdleUpdateBlocker();
    }

    // Unblock idle updates if hidden
    if (globalDocument.hidden && stopVisibilityIdleBlocker) {
      stopVisibilityIdleBlocker();
      stopVisibilityIdleBlocker = undefined;
    }
  });

  if (!globalDocument.hidden) {
    stopVisibilityIdleBlocker = startIdleUpdateBlocker();
  }
}

const idleReloadTimeout = 30 * 60 * 1000; // 30 min.

export function setupIdleUpdate() {
  isIdleUpdateEnabled = true;

  try {
    if ('BroadcastChannel' in globalWindow) {
      broadcastChannel = new BroadcastChannel(lastActiveTimeStorageKey);
      broadcastChannel.addEventListener('message', () => {
        lastActiveTime = Date.now();
      });
    }
  } catch (error) {
    reportToBugsnag(error);
  }

  setupUserInputIdleBlocker();
  setupVisibilityIdleBlocker();

  setLastActiveTimeThrottled();
  globalWindow.setInstrumentedInterval(maybeReloadIfIdle, idleReloadTimeout);
}

async function maybeReloadIfIdle() {
  if (!isWaitingServiceWorker()) {
    return;
  }

  if (!broadcastChannel) {
    const lastActiveTimeString = localStorage.getItem(lastActiveTimeStorageKey);
    if (lastActiveTimeString) {
      lastActiveTime = Math.max(lastActiveTime, Number(lastActiveTimeString));
    }
  }

  // If lastActiveTime is in the future
  if (Date.now() < lastActiveTime) {
    return;
  }

  // If lastActiveTime is very recent
  if (Date.now() - lastActiveTime < idleReloadTimeout) {
    return;
  }

  await trackInterfaceEvent(InterfaceEventsEnum.IDLE_RELOAD);
  reloadServiceWorker();
}
